"use client"

import * as React from "react"
import { useState, useEffect } from "react"
import { ChevronDown } from "lucide-react"
import { cn } from "@/lib/utils"

interface SidebarProps {
  children: React.ReactNode
  className?: string
  open?: boolean
  onOpenChange?: (open: boolean) => void
  defaultOpen?: boolean
  collapsible?: boolean
}

export const Sidebar: React.FC<SidebarProps> = React.memo(({ 
  children, 
  className, 
  open: controlledOpen, 
  onOpenChange, 
  defaultOpen = false, 
  collapsible = true 
}) => {
  const [open, setOpen] = useState(defaultOpen)
  const isControlled = controlledOpen !== undefined
  const currentOpen = isControlled ? controlledOpen : open

  // Track viewport to enforce desktop behavior
  const [isMobile, setIsMobile] = useState<boolean>(false)
  useEffect(() => {
    const update = () => {
      setIsMobile(window.innerWidth < 768)
    }
    update()
    window.addEventListener("resize", update)
    return () => window.removeEventListener("resize", update)
  }, [])

  // If desktop/tablet, force open and ignore collapse
  const isCollapsible = isMobile && collapsible
  const effectiveOpen = isCollapsible ? currentOpen : true

  const handleOpenChange = (newOpen: boolean) => {
    if (!isCollapsible) return
    if (!isControlled) {
      setOpen(newOpen)
    }
    onOpenChange?.(newOpen)
  }

  return (
    <div className={cn(
      "flex h-full flex-col gap-2 bg-background border-r border-border",
      "w-64 md:w-64 shrink-0",
      className
    )}>
      <div className={cn(
        "flex flex-col gap-2 p-4",
        effectiveOpen ? "flex" : "hidden md:flex"
      )}>
        {children}
      </div>
      {isCollapsible && (
        <button
          className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-primary bg-primary text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 md:hidden fixed bottom-4 right-4 z-50 shadow-lg"
          onClick={() => handleOpenChange(!currentOpen)}
          aria-label={effectiveOpen ? "Close sidebar" : "Open sidebar"}
        >
          <ChevronDown 
            className={cn(
              "h-6 w-6 transition-transform duration-200",
              effectiveOpen ? "rotate-180" : ""
            )} 
          />
        </button>
      )}
    </div>
  )
})

export const SidebarContent: React.FC<{
  children: React.ReactNode
  className?: string
}> = React.memo(({ children, className }) => (
  <div className={cn("flex flex-col gap-2", className)}>
    {children}
  </div>
))

export const SidebarGroup: React.FC<{
  children: React.ReactNode
  className?: string
}> = React.memo(({ children, className }) => (
  <div className={cn("flex flex-col gap-2", className)}>
    {children}
  </div>
))

export const SidebarHeader: React.FC<{
  children: React.ReactNode
  className?: string
}> = React.memo(({ children, className }) => (
  <div className={cn("flex flex-col space-y-2", className)}>
    {children}
  </div>
))

export const SidebarFooter: React.FC<{
  children: React.ReactNode
  className?: string
}> = React.memo(({ children, className }) => (
  <div className={cn("flex flex-col space-y-2", className)}>
    {children}
  </div>
))

export const SidebarInset: React.FC<{
  children: React.ReactNode
  className?: string
}> = React.memo(({ children, className }) => (
  <div className={cn(
    "flex h-full flex-col gap-2 border-r transition-all duration-300 ease-out",
    "data-[state=closed]:w-0 data-[state=open]:w-48",
    className
  )}>
    {children}
  </div>
))

export const SidebarSeparator: React.FC<{
  className?: string
}> = React.memo(({ className }) => (
  <div className={cn("-mx-2 my-2 h-px bg-border", className)} />
))

export const SidebarTrigger: React.FC<{
  children: React.ReactNode
  className?: string
  onClick?: () => void
}> = React.memo(({ children, className, onClick }) => (
  <button
    className={cn(
      "inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-background text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
      className
    )}
    onClick={onClick}
  >
    {children}
  </button>
))

export const SidebarMenu: React.FC<{
  children: React.ReactNode
  className?: string
}> = React.memo(({ children, className }) => (
  <div className={cn("flex flex-col gap-1", className)}>
    {children}
  </div>
))

export const SidebarMenuItem: React.FC<{
  children: React.ReactNode
  className?: string
  onClick?: () => void
}> = React.memo(({ children, className, onClick }) => (
  <button
    className={cn(
      "group flex w-full cursor-pointer items-center gap-2.5 rounded-md px-2 py-2 text-sm font-medium transition-all hover:bg-accent hover:text-accent-foreground",
      className
    )}
    onClick={onClick}
  >
    {children}
  </button>
))

export const SidebarMenuButton: React.FC<{
  children: React.ReactNode
  className?: string
  onClick?: () => void
}> = React.memo(({ children, className, onClick }) => (
  <button
    className={cn(
      "group flex w-full cursor-pointer items-center gap-2.5 rounded-md px-2 py-2 text-sm font-medium transition-all hover:bg-accent hover:text-accent-foreground",
      className
    )}
    onClick={onClick}
  >
    {children}
  </button>
))

export const SidebarMenuSub: React.FC<{
  children: React.ReactNode
  className?: string
}> = React.memo(({ children, className }) => (
  <div className={cn("flex flex-col gap-1", className)}>
    {children}
  </div>
))

export const SidebarMenuSubItem: React.FC<{
  children: React.ReactNode
  className?: string
  onClick?: () => void
}> = React.memo(({ children, className, onClick }) => (
  <div
    className={cn(
      "group flex w-full cursor-pointer items-center gap-2.5 rounded-md px-2 py-2 text-sm font-medium transition-all hover:bg-accent/50 hover:text-accent-foreground",
      className
    )}
    onClick={onClick}
  >
    {children}
  </div>
))

export const SidebarMenuSubButton: React.FC<{
  children: React.ReactNode
  className?: string
  onClick?: () => void
}> = React.memo(({ children, className, onClick }) => (
  <button
    className={cn(
      "group flex w-full cursor-pointer items-center gap-2.5 rounded-md px-2 py-2 text-sm font-medium transition-all hover:bg-accent/50 hover:text-accent-foreground",
      className
    )}
    onClick={onClick}
  >
    {children}
  </button>
))

export const SidebarMenuSubTrigger: React.FC<{
  children: React.ReactNode
  className?: string
  onClick?: () => void
}> = React.memo(({ children, className, onClick }) => (
  <button
    className={cn(
      "group flex w-full cursor-pointer items-center gap-2.5 rounded-md px-2 py-2 text-sm font-medium transition-all hover:bg-accent hover:text-accent-foreground",
      className
    )}
    onClick={onClick}
  >
    {children}
    <ChevronDown className="h-4 w-4 transition-transform duration-200" />
  </button>
))

export const SidebarMenuSubContent: React.FC<{
  children: React.ReactNode
  className?: string
}> = React.memo(({ children, className }) => (
  <div className={cn(
    "overflow-hidden rounded-md px-2 py-1",
    "data-[state=closed]:animate-collapsible-down data-[state=open]:animate-collapsible-up",
    "data-[state=closed]:opacity-0 data-[state=open]:opacity-100",
    "data-[state=closed]:h-0 data-[state=open]:h-auto",
    className
  )}>
    <div className="flex flex-col gap-1">
      {children}
    </div>
  </div>
))

// Custom hook for sidebar state (simple state management)
export const useSidebar = () => {
  const [open, setOpen] = useState(false)
  
  const toggle = () => setOpen(!open)
  const close = () => setOpen(false)
  const openSidebar = () => setOpen(true)

  return {
    open,
    toggle,
    close,
    openSidebar,
  }
}